import React, { useState, useEffect, useRef, useContext, useCallback, Children } from 'react'
import { useLocation } from 'react-router-dom'
import { Calendar, Views, momentLocalizer } from 'react-big-calendar'
import { Text, Button } from 'shared/ui'
import { useMediaQuery, useTheme, CircularProgress, Tooltip } from '@material-ui/core'
import {
  Refresh as RefreshIcon,
  Event as SyncedCalendarIcon,
  AssignmentInd as LinkedLeadIcon,
  Assignment as LinkedIssueIcon,
} from '@material-ui/icons'
import { getStatusColor } from 'shared/utils/getStyleProgressStatus'
import qs from 'query-string'
import moment from 'moment'
import _ from 'lodash'
import useStyles from './styles'

import 'react-big-calendar/lib/css/react-big-calendar.css'
import { activityStatuses } from 'shared/utils'

const localizer = momentLocalizer(moment)

// Shared ±7-day padding window: the month grid needs events from the tail of the
// previous month / head of the next to fill its leading/trailing visible day cells.
// Exported so a controlling parent (apps/calendar/CalendarPage) can compute the same
// fetch range from its own lifted `currentMonth` state instead of re-deriving it.
export const getFirstAndLastDates = (currentMonth) => {
  const firstDate = moment(currentMonth).startOf('month').subtract(7, "days").format('YYYY-MM-DD')
  const lastDate = moment(currentMonth).endOf('month').add(7, "days").format('YYYY-MM-DD')

  return { firstDate, lastDate }
}

// Refresh / Back / Today / Next — the grid's own toolbar renders this row internally
// (uncontrolled mode, below); a controlling parent that hides the internal toolbar
// (see CalendarView's `currentMonth` prop) renders this same row itself, elsewhere in
// its layout, wired to its own navigation state. `onNavigate` takes the RBC action
// string ('PREV' | 'TODAY' | 'NEXT') either way, so both call sites behave identically.
export const CalendarNavButtons = ({ isFetching, refreshData, onNavigate }) => {
  const classes = useStyles()
  return (
    <div className={classes.calendarButtonRow}>
      {refreshData && (
        <Button
          size="normal"
          variant="icon"
          color="muted"
          onClick={refreshData}
          disabled={isFetching}
          loading={isFetching}
          icon={<RefreshIcon style={{ fontSize: 20 }} />}
        />
      )}
      <Button
        size="normal"
        variant="text"
        color="primary"
        kind="arrow-left"
        label="Back"
        onClick={() => onNavigate('PREV')}
      />
      <Button
        size="normal"
        variant="text"
        color="primary"
        label="Today"
        onClick={() => onNavigate('TODAY')}
      />
      <Button
        size="normal"
        variant="text"
        color="primary"
        kind="arrow-right"
        label="Next"
        onClick={() => onNavigate('NEXT')}
        useRightIcon
      />
    </div>
  )
}

export const DateCellWrapper = ({ children, value, currentMonth }) => {
  const location = useLocation()
  const queryParams = qs.parse(location.search)
  const isSelected = queryParams?.date === moment(value).format('YYYY-MM-DD')

  const isWithinSameMonth = moment(value).isSame(moment(currentMonth), 'month')

  return React.cloneElement(Children.only(children), {
    style: {
      ...children.props?.style,
      backgroundColor: isSelected ? 'rgba(255, 111, 0, 0.1)' : !isWithinSameMonth ? '#F0F0F0' : 'white',
    },
  })
}

/** custom event render component */
export const EventView = ({ event }) => {
  const classes = useStyles()
  const badges = event.badges || {}
  const badgeColor = event.labelColor || event.activityTextColor || '#78838d'
  const hasBadges = badges.calendar || badges.lead || badges.issue

  // Only show the tooltip when the single-line title is actually clipped by the pill
  // width — MUI renders no tooltip for an empty `title`.
  const titleRef = useRef(null)
  const [isTruncated, setIsTruncated] = useState(false)
  useEffect(() => {
    const check = () => {
      const el = titleRef.current
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth + 1)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [event.title])

  return (
    <Tooltip title={isTruncated ? (event.title || '') : ''} enterDelay={600} enterNextDelay={600} placement="top">
      <div className={classes.calendarEventView}>
        <div
          className={classes.calendarEventLeftAccent}
          style={{ backgroundColor: event.accentColor || event.backgroundColor }}
        />
        <span
          ref={titleRef}
          style={{ fontSize: '0.8rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: event.labelColor || event.activityTextColor, ...event.activityStyleText, ...(event.labelStrike ? { textDecoration: 'line-through' } : {}) }}
        >
          {event.title}
        </span>
        {hasBadges &&
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0, color: badgeColor, opacity: 0.7 }}>
            {badges.calendar && <SyncedCalendarIcon style={{ fontSize: 11 }} />}
            {badges.lead && <LinkedLeadIcon style={{ fontSize: 11 }} />}
            {badges.issue && <LinkedIssueIcon style={{ fontSize: 11 }} />}
          </span>
        }
      </div>
    </Tooltip>
  )
}

/** main CalendarView component */
export const CalendarView = (props) => {
  const {
    items = [], calendarFields, isFetching, onCardClick, containerRef,
    onCalendarDayClick, onCalendarMonthChange, filterData, refreshData,
    selectedEvent,
    // Optional (event, defaults) => cssColor. When given, drives an event's dot/label
    // colour directly instead of the Activity-Log-specific status/progress fallback —
    // lets a plain-event caller (e.g. apps/calendar) tint by its own status set.
    eventColorFor,
    // Optional (item) => { accent, text }. Fully controls a grid event's colours when
    // given: `accent` = the left bar + the selected pill's fill and border; `text` = the
    // label colour in every state; optional `strike: true` strikes the title through.
    // Takes precedence over eventColorFor / the activity fallback. Used by apps/calendar
    // (owner-hashed accent, black label, grey + struck through when canceled).
    eventAppearanceFor,
    // Optional controlled-navigation pair, used by apps/calendar's CalendarPage so the
    // SAME month state drives both this grid and its sibling Agenda view. When
    // `currentMonth` (a Date) is given, this component renders react-big-calendar as a
    // controlled component (`date` + `toolbar={false}`) and never fires
    // `onCalendarMonthChange` itself — the controlling parent computes its own fetch
    // range from its own state instead. Omit both props for the original, uncontrolled
    // behaviour (own internal month state + its own toolbar row) — every other caller
    // of this component (LeadEventList, ActivityCalendar, IssueCalendar) keeps doing
    // that, untouched.
    currentMonth: controlledMonth,
    onNavigate: onExternalNavigate,
  } = props
  const classes = useStyles()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))
  const innerContainerRef = useRef()
  const clickRef = useRef(null)
  const location = useLocation()
  const queryParams = qs.parse(location.search)

  const isMonthControlled = controlledMonth !== undefined

  const [view, setView] = useState(smDown ? Views.DAY : Views.MONTH)
  const [internalMonth, setInternalMonth] = useState(new Date())
  const currentMonth = isMonthControlled ? controlledMonth : internalMonth
  const [isHeightSet, setIsHeightSet] = useState(false)

  const handleNavigate = (date, navView, action) => {
    if (isMonthControlled) {
      if (onExternalNavigate) onExternalNavigate(date, navView, action)
    } else {
      setInternalMonth(date)
    }
  }

  const calculateRemainingHeight = () => {
    if (containerRef?.current && innerContainerRef.current) {
      const parentHeight = containerRef.current.offsetHeight

      const siblingsHeight = Array.from(containerRef.current.children)
        .filter((child) => child !== innerContainerRef.current)
        .reduce((total, sibling) => total + sibling.offsetHeight, 0)

      const remainingHeight = parentHeight - siblingsHeight
      // innerContainerRef.current.style.height = `${remainingHeight}px`
      innerContainerRef.current.style.height = `${remainingHeight - 32}px`
      if (!isHeightSet) setIsHeightSet(true)

      if (smDown) {
        const parentRect = containerRef.current.getBoundingClientRect()
        const childRect = innerContainerRef.current.getBoundingClientRect()
        const gap = parentRect.bottom - childRect.bottom
        innerContainerRef.current.style.height = `${remainingHeight + (gap * 0.75)}px`
      }
    }
  }

  useEffect(() => {
    setTimeout(() => {
      calculateRemainingHeight()
    }, 50)
    window.addEventListener("resize", calculateRemainingHeight)

    return () => {
      window.removeEventListener("resize", calculateRemainingHeight)

      /**
       * What Is This?
       * This is to prevent a memory leak, in the off chance that you
       * teardown your interface prior to the timed method being called.
       * 
       * copied from the official docs:
       * https://jquense.github.io/react-big-calendar/examples/index.html?path=/docs/props--on-select-event
       */
      window.clearTimeout(clickRef?.current)
    }
  }, [])

  /** handle first occurence — skipped when month-controlled: the parent computes its
   *  own fetch range from its own state and would otherwise get a duplicate/racing
   *  fetch from this one. */
  useEffect(() => {
    if (isMonthControlled) return
    if (!filterData?.calendar_date_from && !filterData?.calendar_date_to) {
      const { firstDate, lastDate } = getFirstAndLastDates(currentMonth)
      if (onCalendarMonthChange) {
        onCalendarMonthChange(firstDate, lastDate)
      }
    }
  }, [filterData])

  /** handle later occurences — same controlled-mode skip as above. */
  useEffect(() => {
    if (isMonthControlled) return
    const { firstDate, lastDate } = getFirstAndLastDates(currentMonth)
    if (onCalendarMonthChange) {
      onCalendarMonthChange(firstDate, lastDate)
    }
  }, [currentMonth])

  useEffect(() => {
    if (smDown) {
      calculateRemainingHeight()

      if (view !== Views.DAY) {
        setView(Views.DAY)
      }
    }
  }, [smDown])

  const onSelectEvent = useCallback((calEvent) => {
    /**
     * Here we are waiting 250 milliseconds (use what you want) prior to firing
     * our method. Why? Because both 'click' and 'doubleClick'
     * would fire, in the event of a 'doubleClick'. By doing
     * this, the 'click' handler is overridden by the 'doubleClick'
     * action.
     * 
     * copied from the official docs:
     * https://jquense.github.io/react-big-calendar/examples/index.html?path=/docs/props--on-select-event
     * 
     * also applies to onSelectSlot below
     */
    window.clearTimeout(clickRef?.current)
    clickRef.current = window.setTimeout(() => {
      if (onCardClick) onCardClick(calEvent?.data)
    }, 250)
  }, [queryParams])

  /** handle on click empty date area */
  const onSelectSlot = useCallback((slotInfo) => {
    window.clearTimeout(clickRef?.current)
    clickRef.current = window.setTimeout(() => {
      if (onCalendarDayClick) onCalendarDayClick(slotInfo)
    }, 250)
  }, [queryParams])

  const onShowMore = useCallback(
    (events, date) => {
      if (onCalendarDayClick) onCalendarDayClick({
        slots: [date]
      })
    },
    [queryParams]
  )

  /** handle on click date header */
  const onDrillDown = useCallback((date) => {
    if (onCalendarDayClick) onCalendarDayClick({
      slots: [date]
    })
  }, [queryParams])

  const onView = useCallback((newView) => setView(newView), [setView])

  const getEventTitle = (item, itemFields = {}) => {
    const {primary, secondary} = itemFields
    let primaryTitle = '', secondaryTitle = ''
    if (primary) primaryTitle = _.get(item, primary) || ''
    if (secondary) secondaryTitle = _.get(item, secondary) || ''
    if (!primaryTitle?.length && !secondaryTitle?.length) {
      return item?.name
    } else {
      let title = primaryTitle
      if (secondaryTitle) title = `${primaryTitle} - ${secondaryTitle}`
      return title.trim()
    }
  }

  const calendarEvents = items?.map(item => {
    const { progressBackgroundColor, progressTextColor } = getStatusColor(item?.status)
    const activityTextColor = activityStatuses[item?.data?.status || 'completed']?.calendarTextColor(item?.data?.activity_time) || 'grey'
    const activityStyleText = activityStatuses[item?.data?.status || 'completed']?.styleText || {}
    const customColor = eventColorFor ? eventColorFor(item) : null
    const appearance = eventAppearanceFor ? eventAppearanceFor(item) : null
    // Right-side marker icons on the pill: on an external calendar, linked to a lead,
    // linked to an issue. Data-driven off the calendar-module event shape — absent on
    // Activity-Log / Lead-scheme callers, so nothing renders there.
    const eventLinks = item?.links || []
    const badges = {
      calendar: Boolean(item?.calendar || item?.provider),
      lead: eventLinks.some(l => l?.target_object_type === 'lead/Lead'),
      issue: eventLinks.some(l => l?.target_object_type === 'project/Issue'),
    }
    return {
      id: item?.uid,
      badges,
      // title: calendarFields?.item?.primary ? item?.[calendarFields?.item?.primary] : item?.name,
      title: getEventTitle(item, calendarFields?.item),
      start: item?.calendar_date,
      end: item?.calendar_date,
      backgroundColor: customColor || (item?.data?.status ? activityTextColor : progressBackgroundColor),
      textColor: customColor ? '#fff' : progressTextColor,
      activityTextColor: customColor || (selectedEvent !== item?.data?.id && activityTextColor),
      // apps/calendar appearance override — consumed by EventView + eventPropGetter.
      accentColor: appearance?.accent || null,
      labelColor: appearance?.text || null,
      labelStrike: Boolean(appearance?.strike),
      activityStyleText,
      data: { ...item }
    }
  })

  const eventPropGetter = (event, start, end, isSelected) => {
    // apps/calendar appearance path: fixed accent (side bar + selected fill/border),
    // caller-controlled label colour in every state, selected pill actually filled.
    if (event.accentColor || event.labelColor) {
      const accent = event.accentColor || 'grey'
      return {
        className: isSelected ? 'custom-selected-event' : 'custom-event',
        style: {
          backgroundColor: isSelected ? accent : 'transparent',
          color: event.labelColor || accent,
          borderRadius: '3px',
          cursor: 'pointer',
          boxShadow: isSelected ? `inset 0 0 0 1.5px ${accent}` : 'none',
          fontWeight: isSelected ? 600 : 400,
        },
      }
    }
    // Selection used to FILL the pill with event.backgroundColor while EventView kept
    // rendering the title in event.activityTextColor — for a status-tinted event those
    // are the same colour, so the selected title vanished into its own background.
    // Keep the background transparent in both states and mark selection with an inset
    // outline + bolder text instead, so the title stays readable.
    const accent = event.activityTextColor || event.backgroundColor || 'grey'
    return {
      className: isSelected ? 'custom-selected-event' : 'custom-event',
      style: {
        backgroundColor: 'transparent',
        color: accent,
        borderRadius: '3px',
        cursor: 'pointer',
        boxShadow: isSelected ? `inset 0 0 0 1.5px ${accent}` : 'none',
        fontWeight: isSelected ? 600 : 400,
      }
    }
  }

  const renderToolbar = ({ label, onNavigate }) => {
    return (
      <div className={classes.calendarToolbar}>
        <div className={classes.calendarMonthLabel}>
          <Text variant="h5">
            <strong>{label}</strong>
          </Text>
          {isFetching && (
            <CircularProgress color="secondary" size={16} />
          )}
        </div>
        <CalendarNavButtons isFetching={isFetching} refreshData={refreshData} onNavigate={onNavigate} />
      </div>
    )
  }

  return (
    <div ref={innerContainerRef} className={isMonthControlled ? classes.calendarGridControlled : undefined}>
      {isHeightSet && (
        <Calendar
          views={['month']}
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          tooltipAccessor={null}
          {...(isMonthControlled ? { date: currentMonth, toolbar: false } : {})}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectSlot}
          onShowMore={onShowMore}
          onView={onView}
          onDrillDown={onDrillDown}
          view={view}
          selectable
          eventPropGetter={eventPropGetter}
          onNavigate={handleNavigate}
          messages={{
            // apps/calendar (month-controlled) reads "+4 more"; other callers keep "View all".
            showMore: (total) => (isMonthControlled ? `+${total} more` : 'View all')
          }}
          components={{
            toolbar: renderToolbar,
            event: EventView,
            dateCellWrapper: (props) => <DateCellWrapper {...props} currentMonth={currentMonth} />
          }}
        />
      )}
    </div>
  )
}

export default CalendarView